import { lazy, type JSX } from 'react';
import { createSelector } from 'reselect';

import ProvidersStep from '~/components/authentication/playnow/steps/DGA/providers';
import { authenticationSelector } from '~/components/authentication/ducks/selectors';
import { STEP_TYPES, ACTIONTYPES } from '~/components/authentication/wizard/utils/constants';
import { StepProps, StepHeaderProps } from '~/components/authentication/wizard/types';
import { clearProvider } from '~/components/authentication/playnow/steps/DGA/ducks';
import { clearAll } from '~/components/payment/ducks/payment';

enum StepRoutes {
	providers = 'providers',
	bonus = 'bonus',
	details = 'details',
	limits = 'deposit-limits',
	tnc = 'terms-conditions',
	depositMethods = 'deposit-methods',
	depositAmount = 'deposit-amount',
	depositResult = 'deposit-result',
	privacy = 'privacy-policy'
}

type PossibleRoutes = (typeof StepRoutes)[keyof typeof StepRoutes];

const FLOW: StepRoutes[] = [
	StepRoutes.providers,
	StepRoutes.bonus,
	StepRoutes.details,
	StepRoutes.tnc,
	StepRoutes.privacy,
	StepRoutes.limits,
	StepRoutes.depositMethods,
	StepRoutes.depositAmount,
	StepRoutes.depositResult
];

const TRANSLATIONKEYS = [
	'GET_STARTED',
	'BONUS_TITLE_PLAY_NOW',
	'DETAILS_TITLE_PLAY_NOW',
	'MOBILE_SEARCH_SKIP',
	'MOBILE_RESPONSIBLE_GAMING_DEPOSIT_LIMITS',
	'MOBILE_RESPONSIBLE_GAMING_LEARN_MORE',
	'DEPOSIT_TITLE_PLAY_NOW',
	'PLAY_NOW_WELCOME',
	'METHOD',
	'MOBILE_HELP_PAGE_SUBMENU_TERMS',
	'SIDE_MENU_PRIVACY_POLICY',
	'MITID_USER_ABORTED',
	'ACC_LOCKED',
	'MOBILE_PLAYER_SUSPEND_TIME_ACCOUNT',
	'MITID_UNEXPECTED_ERROR',
	'MITID_CPR_MATCH_FAILED',
	'MITID_NO_CTX'
] as const;

interface RootState {
	authentication: boolean;
	providers: {
		verification?: {
			step: string;
		};
		sessionCriteria?: {
			showDepositLimits?: boolean;
			showRegistrationForm?: boolean;
			showTermsAndConditionPopup?: boolean;
			isRegistration?: boolean;
			showRegistrationFlow?: boolean;
			shouldShowPrivacyPolicyMessage?: boolean;
		};
	};
	bonuses?: {
		bonusSkipped?: boolean;
	};
}

interface StateProps {
	showBonus: boolean;
	showBonusClose: boolean;
	showDepositLimits: boolean;
	showProvidersSelection: boolean;
	showRegistrationForm: boolean;
	showVerificationScreen: boolean;
	showTermsAndConditionPopup: boolean;
	showDepositScreens: boolean;
	shouldShowPrivacyPolicyMessage: boolean;
}

const stateSelector = createSelector(
	authenticationSelector,
	(state: RootState) => state.providers,
	(state: RootState) => state.bonuses,
	(authenticated: boolean, providers: RootState['providers'], bonuses: RootState['bonuses']) => {
		const sessionCriteria = providers?.sessionCriteria ?? {};
		const {
			showDepositLimits = false,
			showRegistrationForm = false,
			showTermsAndConditionPopup = false,
			shouldShowPrivacyPolicyMessage = false,
			isRegistration = false,
			showRegistrationFlow = false
		} = sessionCriteria;

		const showBonus = !bonuses?.bonusSkipped && showRegistrationFlow;

		return {
			showBonus,
			showBonusClose: authenticated,
			showDepositLimits,
			showProvidersSelection: !authenticated,
			showRegistrationForm,
			showVerificationScreen: !authenticated,
			showTermsAndConditionPopup,
			showDepositScreens: isRegistration || showRegistrationFlow,
			shouldShowPrivacyPolicyMessage: !isRegistration && shouldShowPrivacyPolicyMessage
		};
	}
);

type StepMap = Record<PossibleRoutes, (() => JSX.Element) | React.LazyExoticComponent<() => JSX.Element>>;

const STEPMAP: StepMap = {
	[StepRoutes.providers]: ProvidersStep,
	[StepRoutes.bonus]: lazy(() => import('~/components/authentication/playnow/steps/DGA/bonus')),
	[StepRoutes.details]: lazy(() => import('~/components/authentication/playnow/steps/DGA/details')),
	[StepRoutes.limits]: lazy(() => import('~/components/authentication/playnow/steps/DGA/limits')),
	[StepRoutes.tnc]: lazy(() => import('~/components/authentication/playnow/steps/DGA/tnc')),
	[StepRoutes.privacy]: lazy(() => import('~/components/authentication/playnow/steps/DGA/privacy-policy')),
	[StepRoutes.depositMethods]: lazy(() => import('~/components/authentication/playnow/steps/DGA/deposit/methods')),
	[StepRoutes.depositAmount]: lazy(() => import('~/components/authentication/playnow/steps/DGA/deposit/amount')),
	[StepRoutes.depositResult]: lazy(() => import('~/components/authentication/playnow/steps/DGA/deposit/results'))
};

type StepHeaderPropsExt = StepHeaderProps & {
	text?: (typeof TRANSLATIONKEYS)[number];
	moreAction?: {
		text: (typeof TRANSLATIONKEYS)[number];
	};
};

type StepPropsExt = StepProps & {
	key: PossibleRoutes;
	header?: null | StepHeaderPropsExt;
	subStep?: Array<PossibleRoutes> | boolean;
};

const getSteps = (props = {} as StateProps): Record<PossibleRoutes, StepPropsExt> => ({
	[StepRoutes.providers]: {
		key: StepRoutes.providers,
		header: {
			action: ACTIONTYPES.close
		},
		subStep: false,
		isValid: () => props.showProvidersSelection,
		withSuspense: true,
		type: STEP_TYPES.step,
		skippable: false
	},
	[StepRoutes.bonus]: {
		key: StepRoutes.bonus,
		header: {
			text: 'BONUS_TITLE_PLAY_NOW',
			action: props.showBonusClose ? ACTIONTYPES.close : ACTIONTYPES.none,
			moreAction: {
				type: ACTIONTYPES.skip,
				text: 'MOBILE_SEARCH_SKIP'
			}
		},
		subStep: false,
		isValid: () => props.showBonus,
		withSuspense: true,
		type: STEP_TYPES.step,
		skippable: true
	},
	[StepRoutes.details]: {
		key: StepRoutes.details,
		header: {
			text: 'DETAILS_TITLE_PLAY_NOW',
			action: props.showBonus ? ACTIONTYPES.back : ACTIONTYPES.close
		},
		isValid: () => props.showRegistrationForm,
		type: STEP_TYPES.step,
		skippable: false,
		subStep: [StepRoutes.tnc]
	},
	[StepRoutes.limits]: {
		key: StepRoutes.limits,
		header: {
			text: 'MOBILE_RESPONSIBLE_GAMING_DEPOSIT_LIMITS',
			action: props.showBonus ? ACTIONTYPES.back : ACTIONTYPES.none,
			moreAction: {
				text: 'MOBILE_RESPONSIBLE_GAMING_LEARN_MORE'
			}
		},
		isValid: () => props.showDepositLimits,
		withSuspense: true,
		type: STEP_TYPES.step,
		subStep: true,
		className: 'u-relative'
	},
	[StepRoutes.tnc]: {
		key: StepRoutes.tnc,
		header: {
			text: 'MOBILE_HELP_PAGE_SUBMENU_TERMS',
			action: props.showRegistrationForm ? ACTIONTYPES.back : ACTIONTYPES.none
		},
		isValid: () => props.showTermsAndConditionPopup,
		type: STEP_TYPES.hybrid,
		withSuspense: true,
		subStep: false
	},
	[StepRoutes.privacy]: {
		key: StepRoutes.privacy,
		isValid: () => props.shouldShowPrivacyPolicyMessage,
		header: {
			text: 'SIDE_MENU_PRIVACY_POLICY',
			action: ACTIONTYPES.none
		},
		withSuspense: true,
		subStep: false,
		type: STEP_TYPES.step
	},
	[StepRoutes.depositMethods]: {
		key: StepRoutes.depositMethods,
		isValid: () => props.showDepositScreens,
		header: {
			text: 'METHOD',
			action: props.showBonus ? ACTIONTYPES.back : ACTIONTYPES.close,
			moreAction: {
				type: ACTIONTYPES.skip,
				text: 'MOBILE_SEARCH_SKIP'
			}
		},
		withSuspense: true,
		subStep: [StepRoutes.depositAmount],
		type: STEP_TYPES.step
	},
	[StepRoutes.depositAmount]: {
		key: StepRoutes.depositAmount,
		isValid: () => props.showDepositScreens,
		header: {
			text: 'DEPOSIT_TITLE_PLAY_NOW',
			action: ACTIONTYPES.back
		},
		withSuspense: true,
		subStep: false,
		type: STEP_TYPES.step
	},
	[StepRoutes.depositResult]: {
		key: StepRoutes.depositResult,
		isValid: () => props.showDepositScreens,
		header: {
			text: 'DEPOSIT_TITLE_PLAY_NOW',
			action: ACTIONTYPES.none
		},
		withSuspense: true,
		subStep: false,
		type: STEP_TYPES.step
	}
});

const resetCallbacks = (): (() => void)[] => [clearProvider, clearAll];

export default { FLOW, resetCallbacks, TRANSLATIONKEYS, stateSelector, getSteps, STEPMAP };
